import { useRef } from "react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Copy, Trash2, Plus, Package, Layers, Upload, FileSpreadsheet } from "lucide-react";
import {
  PACKAGE_TYPES, GROUP_OPTIONS, groupBorder, groupBg, groupTint,
} from "@/lib/lineItems";

export function newLineItem() {
  return {
    __key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `li-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    item_number: "", description: "", quantity: 1,
    package_type: "", dimensions: "", weight: "", package_group: "0",
  };
}

function LineItemCard({ item, index, onChange, onDuplicate, onRemove, selected, onToggleSelect, prefix }) {
  const border = groupBorder(item.package_group);
  const bg = groupBg(item.package_group);
  const tint = groupTint(item.package_group);

  const set = (k, v) => onChange(index, { ...item, [k]: v });

  return (
    <div
      data-testid={`${prefix}-row-${index}`}
      className={`border-2 ${border || "border-border"} ${selected ? "ring-2 ring-orange-400 ring-offset-1" : ""}`}
    >
      <div className={`flex items-center gap-3 px-3 sm:px-4 py-2 border-b-2 ${border || "border-border"} ${tint || "bg-muted/40"}`}>
        <Checkbox
          data-testid={`${prefix}-select-${index}`}
          checked={selected}
          onCheckedChange={onToggleSelect}
          className="rounded-none border-2 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
        />
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">
          Item {index + 1}
        </span>
        {item.package_group && item.package_group !== "0" && (
          <span data-testid={`${prefix}-badge-${index}`} className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 text-white ${bg}`}>
            <Package className="h-3 w-3" /> Pkg {item.package_group}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button" variant="ghost" size="icon"
            onClick={() => onDuplicate(index)}
            data-testid={`${prefix}-duplicate-${index}`}
            aria-label="Duplicate row"
            className="h-8 w-8 rounded-none hover:bg-foreground hover:text-background"
            title="Duplicate row"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            type="button" variant="ghost" size="icon"
            onClick={() => onRemove(index)}
            data-testid={`${prefix}-remove-${index}`}
            aria-label="Remove row"
            className="h-8 w-8 rounded-none hover:bg-red-600 hover:text-white"
            title="Remove row"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-12 gap-3">
          <div className="col-span-1 sm:col-span-3 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Item #</Label>
            <Input
              data-testid={`${prefix}-item-number-${index}`}
              value={item.item_number}
              onChange={(e) => set("item_number", e.target.value)}
              placeholder="SKU"
              className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
            />
          </div>
          <div className="col-span-2 sm:col-span-7 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Description *</Label>
            <Input
              data-testid={`${prefix}-item-desc-${index}`}
              value={item.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="What's inside"
              className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
            />
          </div>
          <div className="col-span-1 sm:col-span-2 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Qty</Label>
            <Input
              data-testid={`${prefix}-item-qty-${index}`}
              type="number" min="0"
              value={item.quantity}
              onChange={(e) => set("quantity", e.target.value)}
              className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-12 gap-3">
          <div className="col-span-1 sm:col-span-3 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Pkg Type</Label>
            <Select value={item.package_type || undefined} onValueChange={(v) => set("package_type", v)}>
              <SelectTrigger data-testid={`${prefix}-item-pkgtype-${index}`} className="rounded-none h-10 border-2">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {PACKAGE_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-4 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Dims (L × W × H)</Label>
            <Input
              data-testid={`${prefix}-item-dims-${index}`}
              value={item.dimensions || ""}
              onChange={(e) => set("dimensions", e.target.value)}
              placeholder="30 × 20 × 15 cm"
              className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
            />
          </div>
          <div className="col-span-1 sm:col-span-3 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Weight (kg)</Label>
            <Input
              data-testid={`${prefix}-item-weight-${index}`}
              type="number" min="0" step="0.01"
              value={item.weight}
              onChange={(e) => set("weight", e.target.value)}
              placeholder="0.00"
              className="h-10 rounded-none border-2 focus-visible:ring-orange-500"
            />
          </div>
          <div className="col-span-2 sm:col-span-2 space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground whitespace-nowrap">Group</Label>
            <Select value={item.package_group || "0"} onValueChange={(v) => set("package_group", v)}>
              <SelectTrigger data-testid={`${prefix}-item-group-${index}`} className="rounded-none h-10 border-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupTotals({ items }) {
  const groups = new Map();
  items.forEach((it) => {
    if (!it.description) return;
    const key = it.package_group && it.package_group !== "0" ? String(it.package_group) : "_none";
    const cur = groups.get(key) || { count: 0, qty: 0, weight: 0 };
    cur.count += 1;
    cur.qty += Number(it.quantity) || 0;
    cur.weight += Number(it.weight) || 0;
    groups.set(key, cur);
  });
  if (groups.size === 0) return null;
  const sorted = [...groups.entries()].sort(([a], [b]) => (a === "_none" ? 1 : b === "_none" ? -1 : Number(a) - Number(b)));
  return (
    <div data-testid={`${"group"}-totals`} className="border-2 border-border bg-muted/40 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2 mb-3">
        <Layers className="h-3.5 w-3.5" /> Group Totals
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {sorted.map(([key, t]) => {
          const isNone = key === "_none";
          const bg = isNone ? "bg-zinc-400" : groupBg(key);
          const label = isNone ? "Ungrouped" : `Pkg ${key}`;
          return (
            <div key={key} className="flex items-center gap-3 border-2 border-border bg-card px-3 py-2">
              <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 text-white ${bg}`}>{label}</span>
              <div className="text-xs">
                <span className="font-bold">{t.count}</span> {t.count === 1 ? "row" : "rows"} &middot;{" "}
                <span className="font-bold">{t.qty}</span> qty &middot;{" "}
                <span className="font-bold">{t.weight.toFixed(2)}</span> kg
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LineItemsSection({ items, setItems, prefix = "li" }) {
  const fileRef = useRef(null);
  const selectedSet = new Set(items.map((it, i) => (it.__selected ? i : null)).filter((v) => v !== null));

  const onChange = (i, next) => setItems(items.map((it, idx) => (idx === i ? next : it)));
  const onRemove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const onDuplicate = (i) => {
    const clone = { ...items[i], __key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `li-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, __selected: false };
    const next = [...items];
    next.splice(i + 1, 0, clone);
    setItems(next);
  };
  const onToggleSelect = (i) => setItems(items.map((it, idx) => (idx === i ? { ...it, __selected: !it.__selected } : it)));
  const addRow = () => setItems([...items, newLineItem()]);

  const bulkAssign = (groupValue) => {
    if (selectedSet.size === 0) return;
    setItems(items.map((it, idx) => (selectedSet.has(idx) ? { ...it, package_group: groupValue, __selected: false } : it)));
  };
  const clearSelection = () => setItems(items.map((it) => ({ ...it, __selected: false })));

  const normalizeKey = (k) => String(k || "").toLowerCase().replace(/[\s_-]+/g, "");
  const fieldFromRow = (row, ...aliases) => {
    for (const key of Object.keys(row)) {
      if (aliases.map(normalizeKey).includes(normalizeKey(key))) return row[key];
    }
    return "";
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!rows.length) { toast.error("Sheet is empty"); return; }
      const parsed = rows.map((row) => {
        const description = String(fieldFromRow(row, "description", "desc", "item description", "product") || "").trim();
        const group = fieldFromRow(row, "package group", "group", "pkg group", "pkg", "package");
        const groupNum = group === "" || group == null ? "0" : String(parseInt(group, 10) || 0);
        return {
          __key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `li-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          item_number: String(fieldFromRow(row, "item number", "item#", "item", "sku", "code") || "").trim(),
          description,
          quantity: Number(fieldFromRow(row, "quantity", "qty", "count")) || 1,
          package_type: String(fieldFromRow(row, "package type", "pkg type", "type", "packaging") || "").trim(),
          dimensions: String(fieldFromRow(row, "dimensions", "dims", "dim", "size", "lwh", "l x w x h", "l*w*h") || "").trim(),
          weight: fieldFromRow(row, "weight", "weight (kg)", "kg") === "" ? "" : Number(fieldFromRow(row, "weight", "weight (kg)", "kg")) || "",
          package_group: groupNum,
        };
      }).filter((it) => it.description);
      if (!parsed.length) { toast.error("No rows with a Description column found"); return; }
      // Replace items if only the default empty row exists, else append
      const isEmptyDefault = items.length === 1 && !items[0].description && !items[0].item_number;
      setItems(isEmptyDefault ? parsed : [...items, ...parsed]);
      toast.success(`Imported ${parsed.length} row${parsed.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error("Could not read file", { description: err.message });
    }
  };

  return (
    <section className="border-2 border-border bg-card">
      <div className="px-4 sm:px-6 py-3 border-b-2 border-border flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Line Items</div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            onChange={importExcel}
            className="hidden"
            data-testid={`${prefix}-file-input`}
          />
          <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid={`${prefix}-import`} className="rounded-none border-2 h-9 text-xs uppercase tracking-[0.15em] font-bold">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Import Excel
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={addRow} data-testid={`${prefix}-add`} className="rounded-none border-2 h-9 text-xs uppercase tracking-[0.15em] font-bold">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add row
          </Button>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        <p className="text-[11px] text-muted-foreground flex items-start gap-2">
          <Package className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Tick rows and use the bar below to bulk-assign them to a package group. Grouped rows share a colored stripe. Use{" "}
          <span className="font-bold">Import Excel</span> to bring in rows from a spreadsheet with columns like
          Item #, Description, Quantity, Package Type, Dimensions, Weight, Group.
        </p>

        {selectedSet.size > 0 && (
          <div data-testid={`${prefix}-bulk-bar`} className="border-2 border-orange-600 bg-orange-50 dark:bg-orange-950 p-3 flex items-center gap-3 flex-wrap sticky top-2 z-10">
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-orange-900 dark:text-orange-200">
              {selectedSet.size} selected
            </div>
            <Select onValueChange={(v) => bulkAssign(v)}>
              <SelectTrigger data-testid={`${prefix}-bulk-group`} className="rounded-none h-9 border-2 w-40 bg-background">
                <SelectValue placeholder="Assign group..." />
              </SelectTrigger>
              <SelectContent>
                {GROUP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="sm" onClick={clearSelection} data-testid={`${prefix}-bulk-clear`} className="rounded-none border-2 h-9 text-xs uppercase tracking-[0.15em] font-bold">
              Clear
            </Button>
          </div>
        )}

        {items.map((it, i) => (
          <LineItemCard
            key={it.__key || i}
            index={i}
            item={it}
            onChange={onChange}
            onDuplicate={onDuplicate}
            onRemove={onRemove}
            selected={!!it.__selected}
            onToggleSelect={() => onToggleSelect(i)}
            prefix={prefix}
          />
        ))}

        <GroupTotals items={items} />
      </div>
    </section>
  );
}
